import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Grid, File, ChevronDown, ChevronUp } from 'react-feather';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../../../../../config';

import ReportShell from '../../shared/ReportShell';
import ReportFilterBar from '../../shared/ReportFilterBar';
import ReportPagination from '../../shared/ReportPagination';
import useReportLookups from '../../shared/useReportLookups';
import useReportAccess from '../../shared/useReportAccess';

const PAGE_SIZE = 50;
const TODAY = new Date().toISOString().split('T')[0];

const yesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
};

const num = (v) => (typeof v === 'number' ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
const pct = (v) => (v != null ? `${Number(v).toFixed(2)}%` : '-');

const SUMMARY_COLUMNS = [
    { label: 'Tenant', key: 'tenant_name' },
    { label: 'Tcode', key: 'tenant_code' },
    { label: 'Location', key: 'property_name' },
    { label: 'Category', key: 'category_name' },
    { label: 'Gross', key: 'gross_sales', format: 'number' },
    { label: 'Tax', key: 'tax', format: 'number' },
    { label: 'Net', key: 'net_sales', format: 'number' },
    { label: 'Txn', key: 'txn' },
    { label: 'ATV', key: 'atv', format: 'number' },
    { label: 'Growth Daily %', key: 'growth_daily', format: 'percent' },
];

const initialFilters = {
    startDate: yesterday(),
    endDate: yesterday(),
    property_id: '',
    category_id: '',
    subcategory_id: '',
    group_name: '',
    selectedTenants: [],
};

const DaywiseSalesTrendReportView = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const [reportData, setReportData] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const [expandedTenants, setExpandedTenants] = useState({});
    const [dailyDataMap, setDailyDataMap] = useState({});
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [pdfMenuOpen, setPdfMenuOpen] = useState(false);
    const exportRef = useRef(null);
    const pdfRef = useRef(null);

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        categoryId: filters.category_id,
        subcategoryId: filters.subcategory_id,
        groupName: filters.group_name,
    });
    const { concessionaireId, propertyAccessNames } = lookups;
    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const isAnyExpanded = Object.values(expandedTenants).some(Boolean);

    useEffect(() => {
        const onDocClick = (e) => {
            if (exportRef.current && !exportRef.current.contains(e.target)) setExportMenuOpen(false);
            if (pdfRef.current && !pdfRef.current.contains(e.target)) setPdfMenuOpen(false);
        };
        document.addEventListener('click', onDocClick, true);
        return () => document.removeEventListener('click', onDocClick, true);
    }, []);

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id, concessionaire_id: concessionaireId, module: 'Reports', task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'category_id') { next.subcategory_id = ''; next.selectedTenants = []; }
            if (key === 'property_id') { next.selectedTenants = []; }
            if (key === 'subcategory_id') { next.selectedTenants = []; }
            if (key === 'group_name') { next.selectedTenants = []; }
            return next;
        });
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setExpandedTenants({});
        setDailyDataMap({});
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setError('');
        setSort({ key: '', dir: 'asc' });
        setExpandedTenants({});
        setDailyDataMap({});
    };

    const generateReport = async () => {
        setLoading(true);
        setError('');
        setPage(0);
        setExpandedTenants({});
        setDailyDataMap({});
        const payload = {
            concessionaire_id: concessionaireId,
            property_access: propertyAccessNames,
            start_date: filters.startDate,
            end_date: filters.endDate,
            group_name: filters.group_name,
            category_name: nameOf(lookups.categoryOptions, filters.category_id),
            property_name: nameOf(lookups.propertyOptions, filters.property_id),
            subcategory_name: nameOf(lookups.subcategoryOptions, filters.subcategory_id),
            tenant_id: filters.selectedTenants,
            tenant_name: '',
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/getdailysalestrendreport/summary`, payload, { headers });
            setReportData(Array.isArray(res.data?.tenants) ? res.data.tenants : []);
            setHasRun(true);
            insertToLog(' generated Daily Sales Trend report');
        } catch (err) {
            console.error('daily sales trend report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate Daily Sales Trend report'));
        } finally {
            setLoading(false);
        }
    };

    const fetchDailyData = async (tenantId) => {
        if (dailyDataMap[tenantId]) return;
        try {
            const res = await axios.post(`${config.hostname}/reports/getdailysalestrendreport/tenant`, {
                concessionaire_id: concessionaireId,
                tenant_id: tenantId,
                start_date: filters.startDate,
                end_date: filters.endDate,
                property_name: nameOf(lookups.propertyOptions, filters.property_id),
            }, { headers });
            setDailyDataMap((prev) => ({ ...prev, [tenantId]: res.data?.daily_sales || [] }));
        } catch {
            toast.error(t('Failed to load daily data'));
        }
    };

    const toggleExpandTenant = (tenantId) => {
        setPage(0);
        setExpandedTenants((prev) => {
            const willExpand = !prev[tenantId];
            if (willExpand && !dailyDataMap[tenantId]) fetchDailyData(tenantId);
            return { ...prev, [tenantId]: willExpand };
        });
    };

    const requestSort = (key) => {
        setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
        setPage(0);
    };

    const sortedSummary = useMemo(() => {
        const data = [...reportData];
        if (!sort.key) return data;
        return data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            if (typeof av === 'number' && typeof bv === 'number') return sort.dir === 'asc' ? av - bv : bv - av;
            return sort.dir === 'asc' ? String(av ?? '').localeCompare(String(bv ?? '')) : String(bv ?? '').localeCompare(String(av ?? ''));
        });
    }, [reportData, sort]);

    const flattenedRows = useMemo(() => {
        const rows = [];
        sortedSummary.forEach((summary) => {
            rows.push({ type: 'summary', tenant_id: summary.tenant_id, data: summary });
            if (expandedTenants[summary.tenant_id]) {
                (dailyDataMap[summary.tenant_id] || []).forEach((d) => rows.push({ type: 'daily', tenant_id: summary.tenant_id, data: d }));
            }
        });
        return rows;
    }, [sortedSummary, expandedTenants, dailyDataMap]);

    const pageCount = Math.ceil(flattenedRows.length / PAGE_SIZE);
    const currentPageRows = flattenedRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const fields = [
        { key: 'startDate', label: t('Start date'), type: 'date', role: 'startDate', max: TODAY },
        { key: 'endDate', label: t('End date'), type: 'date', role: 'endDate', max: TODAY },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'category_id', label: t('Category'), type: 'select', options: lookups.categoryOptions, placeholder: t('All categories') },
        { key: 'subcategory_id', label: t('Sub category'), type: 'select', options: lookups.subcategoryOptions, placeholder: t('All sub categories') },
        { key: 'group_name', label: t('Group'), type: 'select', options: lookups.groupOptions, placeholder: t('All groups') },
        { key: 'selectedTenants', label: t('Tenants'), type: 'multiselect', options: lookups.tenantOptions, placeholder: t('All tenants'), span: 2 },
    ];

    // ── Exports (preserved) ─────────────────────────────────────────
    const buildExportMetadata = () => {
        const meta = filters.startDate === filters.endDate ? [[t('Date'), filters.startDate]] : [[t('From Date'), filters.startDate], [t('To Date'), filters.endDate]];
        meta.push([t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')]);
        meta.push([t('Category'), nameOf(lookups.categoryOptions, filters.category_id) || t('All')]);
        meta.push([t('Subcategory'), nameOf(lookups.subcategoryOptions, filters.subcategory_id) || t('All')]);
        meta.push([t('Group'), filters.group_name || t('All')]);
        meta.push([t('Exported By'), login_name]);
        meta.push([t('Exported On'), new Date().toLocaleString()]);
        return meta;
    };

    const buildExportRows = () => {
        const rows = [];
        const includeDate = isAnyExpanded;
        currentPageRows.forEach((row) => {
            if (row.type !== 'summary') return;
            const r = row.data;
            const summaryRow = { Tenant: r.tenant_name, Tcode: r.tenant_code, Location: r.property_name, Category: r.category_name };
            if (includeDate) summaryRow.Date = '-';
            Object.assign(summaryRow, { Gross: r.gross_sales, Tax: r.tax, Net: r.net_sales, Txn: r.txn, ATV: r.atv, 'Growth Daily %': r.growth_daily });
            rows.push(summaryRow);
            if (expandedTenants[r.tenant_id]) {
                (dailyDataMap[r.tenant_id] || []).forEach((d) => rows.push({
                    Tenant: '', Tcode: '', Location: '', Category: '', Date: d.date,
                    Gross: d.gross_sales, Tax: d.tax, Net: d.net_sales, Txn: d.txn, ATV: d.atv, 'Growth Daily %': d.growth_daily,
                }));
            }
        });
        return rows;
    };

    const exportToExcel = () => {
        const rows = buildExportRows();
        if (!rows.length) return;
        const ws = XLSX.utils.aoa_to_sheet([]);
        XLSX.utils.sheet_add_aoa(ws, [[t('Daily Sales Trend Report')]], { origin: 'A1' });
        const metadata = buildExportMetadata();
        XLSX.utils.sheet_add_aoa(ws, metadata, { origin: 'A3' });
        const tableStartRow = metadata.length + 5;
        const head = Object.keys(rows[0]);
        XLSX.utils.sheet_add_aoa(ws, [head.map((h) => t(h))], { origin: `A${tableStartRow}` });
        XLSX.utils.sheet_add_aoa(ws, rows.map((r) => head.map((h) => r[h] ?? '')), { origin: `A${tableStartRow + 1}` });
        ws['!cols'] = head.map(() => ({ wch: 18 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Daily Sales Trend');
        XLSX.writeFile(wb, 'DailySalesTrendReport.xlsx');
    };

    const exportToPDF = () => {
        const rows = buildExportRows();
        if (!rows.length) return;
        const doc = new jsPDF('l', 'pt', 'a4');
        doc.setFontSize(14);
        doc.text(t('Daily Sales Trend Report'), 40, 30);
        doc.setFontSize(9);
        let y = 55;
        buildExportMetadata().forEach(([k, v]) => { doc.text(`${k}: ${v}`, 40, y); y += 14; });
        const head = Object.keys(rows[0]);
        const body = rows.map((r) => head.map((h) => (typeof r[h] === 'number' ? num(r[h]) : (r[h] ?? ''))));
        doc.autoTable({ startY: y + 10, head: [head.map((h) => t(h))], body, styles: { fontSize: 8 }, theme: 'grid', headStyles: { fillColor: [40, 40, 40], textColor: 255 } });
        doc.save('DailySalesTrendReport.pdf');
    };

    const exportExpandedRows = (rows, format) => {
        if (!rows?.length) return;
        const head = ['Tenant', 'Tcode', 'Location', 'Category', 'Date', 'Gross', 'Tax', 'Net', 'Txn', 'ATV', 'Growth Daily %'];
        const body = rows.map((r) => [
            r.row_type === 'summary' ? r.tenant_name : '',
            r.row_type === 'summary' ? r.tenant_code : '',
            r.row_type === 'summary' ? r.property_name : '',
            r.row_type === 'summary' ? r.category_name : '',
            r.row_type === 'daily' ? r.date : '',
            r.gross_sales ?? '', r.tax ?? '', r.net_sales ?? '', r.txn ?? '', r.atv ?? '',
            r.growth_daily != null ? `${Number(r.growth_daily).toFixed(2)}%` : '',
        ]);
        if (format === 'excel') {
            const ws = XLSX.utils.aoa_to_sheet([]);
            XLSX.utils.sheet_add_aoa(ws, [[t('Daily Sales Trend Report (Full Details)')]], { origin: 'A1' });
            const metadata = buildExportMetadata();
            XLSX.utils.sheet_add_aoa(ws, metadata, { origin: 'A3' });
            const tableStartRow = metadata.length + 5;
            XLSX.utils.sheet_add_aoa(ws, [head.map((h) => t(h))], { origin: `A${tableStartRow}` });
            XLSX.utils.sheet_add_aoa(ws, body, { origin: `A${tableStartRow + 1}` });
            ws['!cols'] = head.map(() => ({ wch: 18 }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Daily Sales Trend');
            XLSX.writeFile(wb, 'DailySalesTrendReport_Full.xlsx');
        } else {
            const doc = new jsPDF('l', 'pt', 'a4');
            doc.setFontSize(14);
            doc.text(t('Daily Sales Trend Report (Full Details)'), 40, 30);
            doc.setFontSize(9);
            let y = 55;
            buildExportMetadata().forEach(([k, v]) => { doc.text(`${k}: ${v}`, 40, y); y += 14; });
            doc.autoTable({ startY: y + 10, head: [head.map((h) => t(h))], body, styles: { fontSize: 8 }, theme: 'grid', headStyles: { fillColor: [40, 40, 40], textColor: 255 } });
            doc.save('DailySalesTrendReport_Full.pdf');
        }
    };

    const handleExport = async (format, mode) => {
        if (!reportData.length) return;
        if (mode === 'visible') {
            if (format === 'excel') exportToExcel(); else exportToPDF();
            return;
        }
        try {
            setLoading(true);
            const res = await axios.post(`${config.hostname}/reports/getdailysalestrendreport/export`, {
                concessionaire_id: concessionaireId,
                start_date: filters.startDate,
                end_date: filters.endDate,
                property_access: propertyAccessNames,
                property_name: nameOf(lookups.propertyOptions, filters.property_id),
                category_name: nameOf(lookups.categoryOptions, filters.category_id),
                subcategory_name: nameOf(lookups.subcategoryOptions, filters.subcategory_id),
                group_name: filters.group_name,
                tenant_id: filters.selectedTenants,
                export_type: 'expanded',
            }, { headers });
            exportExpandedRows(res.data?.rows, format);
        } catch {
            toast.error(t('Failed to export full report'));
        } finally {
            setLoading(false);
            setExportMenuOpen(false);
            setPdfMenuOpen(false);
        }
    };

    const cellValue = (col, r) => {
        if (col.format === 'number') return num(r[col.key]);
        if (col.format === 'percent') return r[col.key] != null ? pct(r[col.key]) : '-';
        return r[col.key] || '';
    };

    const menuItem = (label, onClick) => (
        <button type="button" className="v3-sadmin-multi-item" style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }} onClick={onClick}>
            {label}
        </button>
    );

    if (!allowed) return null;

    const colCount = SUMMARY_COLUMNS.length + 1 + (isAnyExpanded ? 1 : 0);

    return (
        <Fragment>
            <ReportShell
                title={t('Daily Sales Trend Report')}
                subtitle={t('Per-tenant sales with expandable day-by-day detail and growth %.')}
                actions={(
                    <Fragment>
                        <div ref={exportRef} style={{ position: 'relative' }}>
                            <button type="button" className="v3-btn v3-btn-ghost" disabled={!reportData.length} onClick={() => setExportMenuOpen((o) => !o)} title={t('Export to Excel')}>
                                <Grid size={14} /> {t('Excel')} <ChevronDown size={12} />
                            </button>
                            {exportMenuOpen && (
                                <div className="v3-card" style={{ position: 'absolute', right: 0, top: '110%', zIndex: 50, minWidth: 200, padding: 4 }}>
                                    {menuItem(t('Export Current View'), () => { handleExport('excel', 'visible'); setExportMenuOpen(false); })}
                                    {menuItem(t('Export Full Details'), () => handleExport('excel', 'expanded'))}
                                </div>
                            )}
                        </div>
                        <div ref={pdfRef} style={{ position: 'relative' }}>
                            <button type="button" className="v3-btn v3-btn-ghost" disabled={!reportData.length} onClick={() => setPdfMenuOpen((o) => !o)} title={t('Export to PDF')}>
                                <File size={14} /> {t('PDF')} <ChevronDown size={12} />
                            </button>
                            {pdfMenuOpen && (
                                <div className="v3-card" style={{ position: 'absolute', right: 0, top: '110%', zIndex: 50, minWidth: 200, padding: 4 }}>
                                    {menuItem(t('Export Current View'), () => { handleExport('pdf', 'visible'); setPdfMenuOpen(false); })}
                                    {menuItem(t('Export Full Details'), () => handleExport('pdf', 'expanded'))}
                                </div>
                            )}
                        </div>
                    </Fragment>
                )}
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <div className="v3-card v3-report-table-card">
                    <div className="v3-sadmin-table-wrap">
                        <table className="v3-sadmin-table v3-report-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 44 }}>{t('Daily')}</th>
                                    {SUMMARY_COLUMNS.map((col) => (
                                        <Fragment key={col.key}>
                                            <th className="sortable" onClick={() => requestSort(col.key)}>
                                                {t(col.label)}
                                                {sort.key === col.key
                                                    ? (sort.dir === 'asc' ? <ChevronUp size={12} className="v3-sort-ico" /> : <ChevronDown size={12} className="v3-sort-ico" />)
                                                    : <ChevronUp size={12} className="v3-sort-ico idle" />}
                                            </th>
                                            {col.key === 'category_name' && isAnyExpanded && <th>{t('Date')}</th>}
                                        </Fragment>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={colCount}><div className="v3-report-state"><p>{t('Loading…')}</p></div></td></tr>
                                ) : error ? (
                                    <tr><td colSpan={colCount}><div className="v3-report-state error"><h3>{t('Couldn’t load this report')}</h3><p>{error}</p></div></td></tr>
                                ) : currentPageRows.length === 0 ? (
                                    <tr><td colSpan={colCount}><div className="v3-report-state"><h3>{t('No results')}</h3><p>{hasRun ? t('No data for the selected filters.') : t('Choose your filters and select Generate report to view results.')}</p></div></td></tr>
                                ) : currentPageRows.map((row, idx) => {
                                    if (row.type === 'summary') {
                                        const r = row.data;
                                        return (
                                            <tr key={`s-${idx}`} className="v3-trend-summary">
                                                <td>
                                                    <button type="button" className="v3-btn v3-btn-ghost" style={{ padding: '2px 8px' }} onClick={() => toggleExpandTenant(r.tenant_id)}>
                                                        {expandedTenants[r.tenant_id] ? '−' : '+'}
                                                    </button>
                                                </td>
                                                {SUMMARY_COLUMNS.map((col) => (
                                                    <Fragment key={col.key}>
                                                        <td className={col.format === 'number' ? 'num' : ''}>{cellValue(col, r)}</td>
                                                        {col.key === 'category_name' && isAnyExpanded && <td>-</td>}
                                                    </Fragment>
                                                ))}
                                            </tr>
                                        );
                                    }
                                    const d = row.data;
                                    return (
                                        <tr key={`d-${idx}`}>
                                            <td />
                                            {SUMMARY_COLUMNS.map((col) => (
                                                <Fragment key={col.key}>
                                                    <td className={col.format === 'number' ? 'num' : ''}>
                                                        {col.key === 'gross_sales' ? num(d.gross_sales)
                                                            : col.key === 'tax' ? num(d.tax)
                                                                : col.key === 'net_sales' ? num(d.net_sales)
                                                                    : col.key === 'txn' ? d.txn
                                                                        : col.key === 'atv' ? num(d.atv)
                                                                            : col.key === 'growth_daily' ? (d.growth_daily != null ? pct(d.growth_daily) : '-')
                                                                                : ''}
                                                    </td>
                                                    {col.key === 'category_name' && isAnyExpanded && <td><strong>{d.date}</strong></td>}
                                                </Fragment>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={flattenedRows.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('rows')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default DaywiseSalesTrendReportView;
